import React, { FC, useEffect, useState } from "react";
import "./style.scss";
import GoBack from "../../../assets/GoBackApp.svg";
import ArmToArm from "../../../assets/ArmToArm.svg";
import VectorRight from "../../../assets/OpenBankVektor.svg";
import { useNavigate, useLocation } from "react-router-dom";
import { PATH_LIST } from "shared/lib/react-router";
import ReplenishmentDetails from "../components/replenishment-details";
import Currency from "../components/deposit-currency";
import { CurrencyModal } from "../components/currency-modal";
import { useBlockScrollbar } from "hooks/useBlockScrollbar";
import { accountStore, useDepositInfo } from "entities/account";
import { useStore } from "zustand";
import { CURRENCY_FLAGS } from "static/currencies";
import Loading from "widgets/loading";
import {
  useUserContract,
  useUserName,
} from "entities/session/model/sessionModel";
import { useTranslation } from "react-i18next";
import Button from "shared/ui/button";
import Modal from "widgets/auth/modal";
import { toast } from "react-toastify";
import { firebaseLogEvent } from "app/libs/firebase";
import { fbLogEvent, FB_EVENTS } from "utils/fbEvents";
import TermsCheckbox from "pages/onboarding/components/terms-checkbox";
import { fxOrderStore } from "entities/fx-order";
import { userVerified } from "helpers/functions";
import { useVerificationNavigation } from "hooks/useNavigateVerification";

const BANK_APP_URLS: { [key: string]: { ios: string; android: string } } = {
  Ameriabank: {
    ios: "my-ameria/id1546373103",
    android: "com.banqr.ameriabank&hl=en",
  },
  ArmSwissBank: {
    ios: "armswiss-mobile-banking/id972577846",
    android: "com.armswissbank.mobilebank&hl=en",
  },
  Ardshinbank: {
    ios: "ardshinbank/id1237344603",
    android: "am.lsoft.mobilebanking.ashb&hl=en",
  },
  Unibank: {
    ios: "unimobile-banking/id1156381731",
    android: "am.lsoft.dev.lsmobilebanking.uniproduction&hl=en",
  },
  Araratbank: {
    ios: "araratmobile/id1585797128",
    android: "am.araratbank.araratbankmobile&hl=en",
  },
  IDBank: {
    ios: "idram-idbank/id558788989",
    android: "am.imwallet.android&hl=en",
  },
  "Acba Bank": {
    ios: "acba-digital/id1515436600",
    android: "am.acba.ACBA_Mobile&hl=en",
  },
  Inecobank: {
    ios: "inecomobile-banking-made-easy/id919137872",
    android: "com.sflpro.inecomobile&hl=en",
  },
  "Evoca Bank": {
    ios: "evocatouch/id970309076",
    android: "am.prometeybank.mobilebank&hl=en",
  },
  "Converse Bank": {
    ios: "converse-mobile/id1577370609",
    android: "am.conversebank.conversebankmobile.release&hl=en",
  },
};

export const DepositPage: FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const initialCurrency = location.state?.currency;
  const [currencyModal, setCurrencyModal] = useState<
    null | "bank" | "currency"
  >(null);
  const { t } = useTranslation("app");
  const { t: err } = useTranslation("errors");
  const {
    isLoading,
    depositInfo: depositAccounts,
    getDepositInfo,
    selectedDepositCurrency,
    selectCurrency,
    requestDeposit,
  } = useStore(accountStore);
  const { from } = useStore(fxOrderStore);
  const [selectedBank, setSelectedBank] = useState<number | null>(null);
  const [depositForm, setDepositForm] = useState(false);
  const [continueVerificationModal, setContinueVerificationModal] =
    useState(false);
  const { navigateVerification } = useVerificationNavigation();
  const [changeFromModal, setChangeFromModal] = useState(false);
  const depositInfo = useDepositInfo(selectedDepositCurrency, selectedBank);
  const currencies = new Set(depositAccounts?.map((v) => v.currency.code));
  const userName = useUserName();
  const contract = useUserContract();
  const [depositAmount, setDepositAmount] = useState<number>();
  const [madeDeposit, setMadeDeposit] = useState(false);

  const handleNavigate = () => {
    navigate(-1);
  };

  useEffect(() => {
    getDepositInfo();
  }, []);

  useEffect(() => {
    if (initialCurrency) {
      selectCurrency("deposit")(initialCurrency);
    } else if (from) {
      selectCurrency("deposit")(from);
    }
  }, [initialCurrency]);

  useEffect(() => {
    if (!userVerified()) {
      setContinueVerificationModal(true);
    }
  }, []);

  useEffect(() => {
    if (!depositAccounts.length || !selectedDepositCurrency) return;

    const accountsForCurrency = depositAccounts.filter(
      (d) => d.currency.code === selectedDepositCurrency,
    );
    const bankIds = accountsForCurrency
      .map((d) => d.info_source?.id)
      .filter((id): id is number => id != null);

    if (selectedBank && bankIds.includes(selectedBank)) return;

    setSelectedBank(bankIds[0] ?? null);
  }, [depositAccounts, selectedDepositCurrency]);

  useBlockScrollbar(!!currencyModal || depositForm, [
    currencyModal,
    depositForm,
  ]);

  const handleBankChange = () => {
    setDepositForm(false);
    setCurrencyModal("bank");
    setChangeFromModal(true);
  };

  const handleCurrencyChange = () => {
    setDepositForm(false);
    setCurrencyModal("currency");
    setChangeFromModal(true);
  };

  const handlePropChange = (value: any) => {
    const isBankModal = currencyModal === "bank";

    if (isBankModal) {
      setSelectedBank(value);
      if (changeFromModal) {
        setChangeFromModal(false);
        setDepositForm(true);
      }
    } else {
      const previousBank = selectedBank;
      selectCurrency("deposit")(value);
      const accountsForCurrency = depositAccounts.filter(
        (d) => d.currency.code === value,
      );
      if (
        previousBank &&
        accountsForCurrency.some((d) => d.info_source.id === previousBank)
      ) {
        setSelectedBank(previousBank);
      } else {
        setSelectedBank(
          accountsForCurrency.map((d) => d.info_source)[0]?.id || null,
        );
      }
      if (changeFromModal) {
        setChangeFromModal(false);
        setDepositForm(true);
      }
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDepositAmount(+e.target.value);
  };

  const handleDepositRequest = () => {
    if (!userVerified()) {
      setContinueVerificationModal(true);
      return;
    }

    if (isNaN(depositAmount as number)) return;
    const amount = +(depositAmount || 0);
    requestDeposit(
      {
        amount,
        currency: selectedDepositCurrency,
        info_source: selectedBank!,
      },
      (resp: any) => {
        if (resp.error) {
          toast.error(err(String(resp.error.code)));
        } else {
          setDepositForm(false);
          firebaseLogEvent("deposit_request");
          void fbLogEvent(FB_EVENTS.initiatedCheckout);
          navigate(PATH_LIST.account);
          toast.success("Request sent");
        }
      },
    );
  };

  const handleBankAppClick = () => {
    if (!depositInfo?.info_source?.name) return;

    const bankName = depositInfo.info_source.name;
    const bankAppInfo = BANK_APP_URLS[bankName];

    if (!bankAppInfo) {
      return;
    }

    const isIOS = /iPad|iPhone|iPod|Macintosh|MacIntel|MacPPC|Mac68K/.test(
      navigator.userAgent,
    );
    const isAndroid = /Android/.test(navigator.userAgent);

    if (isIOS) {
      window.location.href = `https://apps.apple.com/app/${bankAppInfo.ios.replace(
        "://",
        "",
      )}`;
    } else if (isAndroid) {
      window.location.href = `https://play.google.com/store/apps/details?id=${bankAppInfo.android}`;
    }
  };

  if (!depositInfo || isLoading) {
    return (
      <div className="deposit">
        <div className="deposit-header">
          <img
            className="go-back-portfolio"
            onClick={handleNavigate}
            src={GoBack}
            alt="Go Back"
          />
          <p>{t("deposit")}</p>
        </div>

        {isLoading ? <Loading /> : <p>{t("noData")}</p>}
      </div>
    );
  }

  return (
    <div className="deposit">
      <div className="deposit-header">
        <img
          className="go-back-portfolio"
          onClick={handleNavigate}
          src={GoBack}
          alt="Go Back"
        />
        <p>{t("fundAccount")}</p>
      </div>

      <div className="deposit-main">
        {/* {selectedDepositCurrency === "USD" && (
        <BargainingAlert variant="info" text={t("depositAlert")} />
        )} */}

        <Currency
          image={CURRENCY_FLAGS[selectedDepositCurrency]}
          currency={selectedDepositCurrency}
          onClick={() => setCurrencyModal("currency")}
        />

        <Currency
          image={depositInfo.info_source.image}
          onClick={() => setCurrencyModal("bank")}
          bank={depositInfo.info_source.name}
        />

        <div className="deposit-main-alert">
          <p>
            <img src={ArmToArm} alt="Arm" />
            {t("armeniaToArmenia")}
          </p>
          <span>
            {t("internationalTransfersSupport1")}{" "}
            <span onClick={() => navigate(PATH_LIST.chat)}>
              {t("internationalTransfersSupport2")}
            </span>
          </span>
        </div>

        <div className="copy-transfer-details">
          <p>{t("copyTransferDetails")}</p>

          <div className="copy-transfer-details__content">
            <ReplenishmentDetails
              nameOfDetail={t("accountNumber")}
              detail={depositInfo.accountNumber}
            />
            <ReplenishmentDetails
              nameOfDetail={t("recipient")}
              detail={depositInfo.recipient}
            />
            <ReplenishmentDetails
              nameOfDetail={t("purpose")}
              // detail={t("deposit-purpose", {
              //   contract: contract,
              // })}
              detail={`Deposit brokerage account ${contract}`}
            />
          </div>
        </div>

        <div className="inform-about-deposit">
          {BANK_APP_URLS[depositInfo?.info_source?.name] && (
            <div className="inform-about-deposit__application">
              <p>{t("goToBankApp")}</p>
              <button onClick={handleBankAppClick}>
                <img src={VectorRight} alt="VectorRight" />
                <img
                  src={depositInfo.info_source.image}
                  alt={depositInfo.info_source.name}
                />
              </button>
            </div>
          )}

          <TermsCheckbox
            className="inform-about-deposit__checkbox"
            term={t("iMadeDeposit")}
            htmlForId="1"
            isChecked={madeDeposit}
            onChange={() => setMadeDeposit(!madeDeposit)}
          />

          <Button
            className="inform-about-deposit__btn"
            children={t("confirm")}
            disabled={!madeDeposit}
            variant="dark"
            onClick={() => {
              if (!userVerified()) {
                setContinueVerificationModal(true);
                return;
              }
              setDepositForm(true);
            }}
          />
        </div>
      </div>

      {currencyModal && (
        <CurrencyModal
          onClose={() => setCurrencyModal(null)}
          onCurrencyChange={handlePropChange}
          selectedCurrency={selectedDepositCurrency}
          selectedBank={selectedBank}
          currencies={Array.from(currencies)}
          type={currencyModal}
          banks={
            currencyModal === "bank"
              ? depositAccounts
                  .filter((d) => d.currency.code === selectedDepositCurrency)
                  .map((d) => d.info_source)
              : undefined
          }
        />
      )}

      {depositForm && (
        <Modal
          customModal={"inform-about-deposit-modal"}
          onClick={() => {
            handleDepositRequest();
          }}
          onHide={() => setDepositForm(false)}
          title={t("depositConfirmation")}
          text={t("toSpeedUpProcessing")}
          showSecondBtn={false}
          customOutlet="customOutlet"
          depositFields={{
            bank: depositInfo.info_source.name,
            logo: depositInfo.info_source.image,
            onBankChange: handleBankChange,
            currency: selectedDepositCurrency,
            icon: CURRENCY_FLAGS[selectedDepositCurrency],
            onCurrencyChange: handleCurrencyChange,
          }}
          btnText={t("confirm")}
          onChange={handleInputChange}
          depositAmount={depositAmount}
        />
      )}

      {continueVerificationModal && (
        <Modal
          customModal="modalLogOut welcomeModal"
          onClick={() => {
            window.history.replaceState({}, "");
            navigateVerification();
          }}
          onHide={() => {
            window.history.replaceState({}, "");
            setContinueVerificationModal(false);
            navigate(PATH_LIST.account);
          }}
          title={t("completeVerification")}
          text={t("toFullyActivateVerification")}
          btnText={t("completeVerificationBtn")}
          showSecondBtn={false}
          customOutlet="customOutlet"
        />
      )}
    </div>
  );
};
